package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.formatIsoDate
import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.core.util.parallelMap
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.CompanyLink
import br.com.vvrefeicoes.portal.domain.model.CompanyType
import br.com.vvrefeicoes.portal.domain.model.DEFAULT_ADMIN_OTHERS_PERCENT
import br.com.vvrefeicoes.portal.domain.model.Director
import br.com.vvrefeicoes.portal.domain.model.Manager
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.OrgChartStatus
import br.com.vvrefeicoes.portal.domain.model.OrgChartVersion
import br.com.vvrefeicoes.portal.domain.model.Supervisor
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartSnapshot
import br.com.vvrefeicoes.portal.domain.repository.UserCandidate
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteOrgChartRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : OrgChartRepository {
    private val loadMutex = Mutex()
    private var cachedSnapshot: OrgChartSnapshot? = null
    private var cachedToken: String? = null

    override suspend fun loadInitial(): OrgChartSnapshot =
        loadMutex.withLock {
            val token = authRepository.session.value?.token
            cachedSnapshot?.takeIf { cachedToken == token }?.let { return it }
            val snapshot = fetchInitial()
            cachedToken = token
            cachedSnapshot = snapshot
            snapshot
        }

    private suspend fun fetchInitial(): OrgChartSnapshot {
        val current = get<OrgChartSnapshotDto>("/org-chart")
        val versions = get<List<OrgChartVersionDto>>("/org-chart/versions")
        val versionDetails =
            versions.parallelMap { version ->
                get<OrgChartSnapshotDto>("/org-chart/versions/${version.id}").toDomain()
            }
        val draft = runCatching { get<OrgChartSnapshotDto>("/org-chart/draft") }.getOrNull()
        return OrgChartSnapshot(
            versions = versionDetails,
            currentVersion = current.toDomain(),
            draft = draft?.toDomain(),
        )
    }

    private suspend fun invalidate() {
        loadMutex.withLock {
            cachedSnapshot = null
            cachedToken = null
        }
    }

    override suspend fun createDraft(): OrgChartVersion {
        val created = post<OrgChartSnapshotDto>("/org-chart/draft", emptyMap<String, String>()).toDomain()
        invalidate()
        return created
    }

    override suspend fun saveDraft(draft: OrgChartVersion) {
        put<OrgChartSnapshotDto>("/org-chart/draft", draft.data.toDto())
        invalidate()
    }

    override suspend fun publish(
        draft: OrgChartVersion,
        changes: List<String>,
    ): OrgChartVersion {
        val published = post<OrgChartPublishResponseDto>("/org-chart/draft/publish", draft.data.toDto().copy(changes = changes)).toDomain()
        invalidate()
        return published
    }

    override suspend fun discardDraft(id: String) {
        request { httpClient.delete(baseUrl + "/org-chart/draft") { authorize() } }
        invalidate()
    }

    override suspend fun loadSupervisorCandidates(): List<UserCandidate> = loadCandidates("/org-chart/supervisor-candidates")

    override suspend fun loadDirectorCandidates(): List<UserCandidate> = loadCandidates("/org-chart/director-candidates")

    override suspend fun loadManagerCandidates(): List<UserCandidate> = loadCandidates("/org-chart/manager-candidates")

    override suspend fun saveUnitSupervisors(
        companyId: String,
        supervisorIds: List<String>,
    ) {
        request {
            httpClient.put(baseUrl + "/org-chart/supervisors/$companyId") {
                jsonBody(OrgChartSupervisorsRequestDto(supervisorIds))
            }
        }
        invalidate()
    }

    private suspend fun loadCandidates(path: String): List<UserCandidate> = get<List<CandidateDto>>(path).map { UserCandidate(id = it.id, name = it.name, role = it.cargo) }

    private suspend inline fun <reified T> get(path: String): T =
        request {
            httpClient.get(baseUrl + path) { authorize() }
        }.body()

    private suspend inline fun <reified T> post(
        path: String,
        body: Any,
    ): T = request { httpClient.post(baseUrl + path) { jsonBody(body) } }.body()

    private suspend inline fun <reified T> put(
        path: String,
        body: Any,
    ): T = request { httpClient.put(baseUrl + path) { jsonBody(body) } }.body()

    private suspend inline fun request(
        crossinline call: suspend () -> io.ktor.client.statement.HttpResponse,
    ): io.ktor.client.statement.HttpResponse {
        val response = call()
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<OrgChartApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }
}

@Serializable
private data class OrgChartSnapshotDto(
    val version: OrgChartVersionDto,
    val data: OrgChartDataDto,
)

@Serializable
private data class OrgChartSupervisorsRequestDto(
    val supervisorIds: List<String>,
)

@Serializable
private data class OrgChartPublishResponseDto(
    val version: OrgChartVersionDto,
    val data: OrgChartDataDto,
)

@Serializable
private data class OrgChartVersionDto(
    val id: String,
    val number: String,
    val status: String,
    val publishedAt: String? = null,
    val editedAt: String? = null,
    val effectiveFrom: String? = null,
    val effectiveTo: String? = null,
    val owner: String? = null,
)

@Serializable
private data class OrgChartDataDto(
    val directors: List<OrgPersonDto> = emptyList(),
    val managers: List<OrgPersonDto> = emptyList(),
    val supervisors: List<OrgPersonDto> = emptyList(),
    val companies: List<OrgCompanyDto> = emptyList(),
    val managerDirectors: Map<String, Set<String>> = emptyMap(),
    val companyLinks: Map<String, Set<OrgCompanyLinkDto>> = emptyMap(),
    val unitSupervisors: Map<String, Set<String>> = emptyMap(),
    val changes: List<String> = emptyList(),
)

@Serializable
private data class OrgPersonDto(
    val id: String,
    val name: String,
    val inactive: Boolean = false,
    val cargo: String = "",
)

@Serializable
private data class OrgCompanyDto(
    val id: String,
    val organogramaUnidadeId: String = "",
    val name: String,
    val type: String = CompanyType.UPR.name,
    val legalName: String = "",
    val logo: String = "",
    val email: String = "",
    val telcom: String = "",
    val telcel: String = "",
    val cnpj: String = "",
    val qtFuncionarios: Int = 0,
    val cep: String = "",
    val rua: String = "",
    val numero: String = "",
    val bairro: String = "",
    val cidade: String = "",
    val estado: String = "",
    val restoIngesta: Int = 0,
    val linha: String = "0",
    val qtdbutao: Int = 3,
    val tempoAutoEnvioCurto: Int = 2,
    val tempoAutoEnvioLongo: Int = 45,
    val inactive: Boolean = false,
    val adminOthersPercent: Double? = null,
)

@Serializable
private data class OrgChartLinkDto(
    val directorId: String,
    val managerId: String?,
)

private typealias OrgCompanyLinkDto = OrgChartLinkDto

@Serializable
private data class OrgChartApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class CandidateDto(
    val id: String,
    val name: String,
    val cargo: String = "",
)

private fun OrgChartVersionDto.toDomain(data: OrgChartData): OrgChartVersion =
    OrgChartVersion(
        id = id,
        number = number,
        status = status.toOrgChartStatus(),
        publishedAt = formatIsoDateTime(publishedAt),
        editedAt = formatIsoDateTime(editedAt),
        effectiveFrom = formatIsoDate(effectiveFrom),
        effectiveTo = formatIsoDate(effectiveTo),
        owner = owner,
        data = data,
    )

private fun OrgChartSnapshotDto.toDomain(): OrgChartVersion = version.toDomain(data.toDomain())

private fun OrgChartPublishResponseDto.toDomain(): OrgChartVersion = version.toDomain(data.toDomain())

private fun OrgChartDataDto.toDomain(): OrgChartData =
    OrgChartData(
        directors = directors.map { Director(it.id, it.name, it.inactive) },
        managers = managers.map { Manager(it.id, it.name, it.inactive) },
        supervisors = supervisors.map { Supervisor(it.id, it.name, it.inactive, it.cargo) },
        companies = companies.map { it.toDomain() },
        managerDirectors = managerDirectors,
        companyLinks = companyLinks.mapValues { (_, links) -> links.map { CompanyLink(it.directorId, it.managerId) }.toSet() },
        unitSupervisors = unitSupervisors,
    )

private fun OrgCompanyDto.toDomain(): Company =
    Company(
        id = id,
        name = name,
        type = type.toCompanyType(),
        legalName = legalName,
        logo = logo,
        email = email,
        commercialPhone = telcom,
        mobilePhone = telcel,
        cnpj = cnpj,
        employeeCount = qtFuncionarios,
        cep = cep,
        street = rua,
        number = numero,
        neighborhood = bairro,
        city = cidade,
        state = estado,
        leftoverMeals = restoIngesta,
        line = linha,
        buttonCount = qtdbutao,
        shortAutoSendTime = tempoAutoEnvioCurto,
        longAutoSendTime = tempoAutoEnvioLongo,
        inactive = inactive,
        organogramaUnidadeId = organogramaUnidadeId,
        adminOthersPercent = adminOthersPercent ?: DEFAULT_ADMIN_OTHERS_PERCENT,
    )

private fun OrgChartData.toDto(): OrgChartDataDto =
    OrgChartDataDto(
        directors = directors.map { OrgPersonDto(it.id, it.name, it.inactive) },
        managers = managers.map { OrgPersonDto(it.id, it.name, it.inactive) },
        supervisors = supervisors.map { OrgPersonDto(it.id, it.name, it.inactive, it.cargo) },
        companies =
            companies.map {
                OrgCompanyDto(
                    id = it.id,
                    organogramaUnidadeId = it.organogramaUnidadeId,
                    name = it.name,
                    type = it.type.name,
                    legalName = it.legalName,
                    logo = it.logo,
                    email = it.email,
                    telcom = it.commercialPhone,
                    telcel = it.mobilePhone,
                    cnpj = it.cnpj,
                    qtFuncionarios = it.employeeCount,
                    cep = it.cep,
                    rua = it.street,
                    numero = it.number,
                    bairro = it.neighborhood,
                    cidade = it.city,
                    estado = it.state,
                    restoIngesta = it.leftoverMeals,
                    linha = it.line,
                    qtdbutao = it.buttonCount,
                    tempoAutoEnvioCurto = it.shortAutoSendTime,
                    tempoAutoEnvioLongo = it.longAutoSendTime,
                    inactive = it.inactive,
                    adminOthersPercent = it.adminOthersPercent,
                )
            },
        managerDirectors = managerDirectors,
        companyLinks = companyLinks.mapValues { (_, links) -> links.map { OrgChartLinkDto(it.directorId, it.managerId) }.toSet() },
        unitSupervisors = unitSupervisors,
    )

private fun String.toOrgChartStatus(): OrgChartStatus =
    when (uppercase()) {
        "DRAFT" -> OrgChartStatus.Draft
        "LEGACY" -> OrgChartStatus.Legacy
        else -> OrgChartStatus.Current
    }

private fun String.toCompanyType(): CompanyType =
    when (uppercase()) {
        CompanyType.TRA.name -> CompanyType.TRA
        CompanyType.LOJ.name -> CompanyType.LOJ
        CompanyType.EVENTOS.name -> CompanyType.EVENTOS
        else -> CompanyType.UPR
    }
